import React, { useState, useCallback, useEffect, useContext, useRef } from 'react';
import JumboContentLayout from '@jumbo/components/JumboContentLayout';
import useJumboTheme from '@jumbo/hooks/useJumboTheme';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as yup from 'yup';
import { Form, Formik, useFormikContext } from 'formik';
import JumboTextField from '@jumbo/components/JumboFormik/JumboTextField';
import JumboSelectField from '@jumbo/components/JumboFormik/JumboSelectField';
import InputMask from 'react-input-mask';
import axios from 'axios';
import { ApiService } from 'app/servicesTwo/ApiService';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import HeaderBreadcrumbs from 'app/layouts/shared/headers/HeaderBreadcrumbs/HeaderBreadcrumbs';
import { PermissionContext } from 'app/contexts/PermissionContext';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import useTokenCheck from '@jumbo/hooks/useTokenCheck';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import JumboSearchClasses from '@jumbo/components/JumboSearchClasses';
import CheckboxSection from 'app/components/IJM/shared/CheckboxSection';
import ContatoCard from 'app/components/IJM/shared/ContatoCard';

const ScrollToFirstError = () => {
    const { errors, submitCount } = useFormikContext();
    useEffect(() => {
        if (submitCount === 0 || Object.keys(errors).length === 0) return;
        const allFields = document.querySelectorAll('[name]');
        for (const field of allFields) {
            if (errors[field.name]) {
                field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                break;
            }
        }
        // Depende só de submitCount: errors é um objeto novo a cada validação de blur,
        // e não queremos rolar a tela de novo a cada campo preenchido, só a cada tentativa de envio.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitCount]);
    return null;
};

// =============================================
// CONSTANTES
// =============================================

const OPCOES_DEFICIENCIAS = ['Auditiva', 'Física', 'Visual', 'Intelectual', 'Outra'];
const OPCOES_ALERGIAS = ['Alimento', 'Medicamento', 'Odores', 'Poeira', 'Pelo de animais', 'Outra'];
const OPCOES_PROBLEMAS_SAUDE = ['Diabetes', 'Asma', 'Epilepsia', 'Hipertensão', 'Bronquite', 'Outra'];
const OPCOES_TRANSTORNOS = ['TEA', 'TDAH', 'TAG', 'Transtorno de aprendizagem', 'Outro'];

// =============================================
// HELPERS
// =============================================

const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toISOString().split('T')[0];
};

const buildContatos = (values, prefix, count) => {
    const result = [];
    for (let i = 1; i <= count; i++) {
        const nome = values[`${prefix}_${i}_nome`];
        if (nome?.trim()) {
            result.push({
                nome,
                parentesco: values[`${prefix}_${i}_parentesco`] || '',
                celular: values[`${prefix}_${i}_celular`] || '',
                ordem: i,
            });
        }
    }
    return result;
};

// =============================================
// INITIAL VALUES
// =============================================

const getInitialValues = () => ({
    // Dados do menor (pessoa)
    nome: '',
    nome_social: '',
    data_nascimento: '',
    genero: '',
    cpf: '',
    rg: '',
    celular: '05511',
    telefone: '',
    email: '',
    pais: 'BR',
    CEP: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    // Dados específicos do menor
    grau_parentesco: '',
    deficiencias: [],
    outra_deficiencia: '',
    alergias: [],
    outra_alergia: '',
    problemas_saude: [],
    outro_problema_saude: '',
    transtornos: [],
    outro_transtorno: '',
    medicacao_continua: false,
    medicamentos: '',
    pode_ir_sozinho: false,
    // Contatos de emergência
    contato_emergencia_1_nome: '',
    contato_emergencia_1_parentesco: '',
    contato_emergencia_1_celular: '',
    contato_emergencia_2_nome: '',
    contato_emergencia_2_parentesco: '',
    contato_emergencia_2_celular: '',
    contato_emergencia_3_nome: '',
    contato_emergencia_3_parentesco: '',
    contato_emergencia_3_celular: '',
    // Pessoas autorizadas
    autorizado_1_nome: '',
    autorizado_1_parentesco: '',
    autorizado_1_celular: '',
    autorizado_2_nome: '',
    autorizado_2_parentesco: '',
    autorizado_2_celular: '',
    autorizado_3_nome: '',
    autorizado_3_parentesco: '',
    autorizado_3_celular: '',
    // Dados do responsável novo (quando aplicável)
    nome_responsavel: '',
    data_nascimento_responsavel: '',
    cpf_responsavel: '',
    rg_responsavel: '',
    celular_responsavel: '05511',
    celular_responsavel_confirmacao: '',
    telefone_responsavel: '',
    email_responsavel: '',
    email_responsavel_confirmacao: '',
    CEP_responsavel: '',
    endereco_responsavel: '',
    numero_responsavel: '',
    complemento_responsavel: '',
    bairro_responsavel: '',
    cidade_responsavel: '',
    uf_responsavel: '',
});

// Mapa de valores -> rótulos do grau de parentesco (mesmos labels dos selects)
const GRAU_PARENTESCO_LABELS = {
    pai: 'Pai/Padrasto',
    mae: 'Mãe/Madrasta',
    avo: 'Avô/Avó',
    tio: 'Tio/Tia',
    irmao: 'Irmão/Irmã',
    representante_legal: 'Representante Legal',
    outro: 'Outro',
};

const getValidationSchema = (mostrarCadastroResponsavelNovo, temResponsavel) =>
    yup.object().shape({
        nome: temResponsavel ? yup.string().required('Nome é obrigatório') : yup.string(),
        data_nascimento: temResponsavel
            ? yup.string().required('Data de nascimento é obrigatória')
            : yup.string(),
        grau_parentesco: temResponsavel
            ? yup.string().required('Grau de parentesco é obrigatório')
            : yup.string(),
        contato_emergencia_1_nome: temResponsavel
            ? yup.string().required('Nome do 1º contato de emergência é obrigatório')
            : yup.string(),
        contato_emergencia_1_celular: temResponsavel
            ? yup.string().required('Celular do 1º contato de emergência é obrigatório')
            : yup.string(),
        nome_responsavel: mostrarCadastroResponsavelNovo
            ? yup.string().required('Nome do responsável é obrigatório')
            : yup.string(),
        data_nascimento_responsavel: mostrarCadastroResponsavelNovo
            ? yup
                  .string()
                  .required('Data de nascimento do responsável é obrigatória')
                  .test('maior18', 'Responsável deve ter pelo menos 18 anos', (val) => {
                      if (!val) return false;
                      const hoje = new Date();
                      const nasc = new Date(val);
                      let idade = hoje.getFullYear() - nasc.getFullYear();
                      if (
                          hoje.getMonth() < nasc.getMonth() ||
                          (hoje.getMonth() === nasc.getMonth() && hoje.getDate() < nasc.getDate())
                      ) {
                          idade--;
                      }
                      return idade >= 18;
                  })
            : yup.string(),
        celular_responsavel: mostrarCadastroResponsavelNovo
            ? yup
                  .string()
                  .required('Celular do responsável é obrigatório')
                  .test('celular-completo', 'Preencha o celular completo do responsável', (val) => {
                      const digitos = (val || '').replace(/\D/g, '');
                      return digitos.length === 14;
                  })
            : yup.string(),
        celular_responsavel_confirmacao: mostrarCadastroResponsavelNovo
            ? yup
                  .string()
                  .required('Confirmação do celular é obrigatória')
                  .test('cel-match-resp', 'Os celulares do responsável devem ser iguais', function (value) {
                      const cel = (this.parent.celular_responsavel || '').replace(/\D/g, '');
                      const conf = (value || '').replace(/\D/g, '');
                      return cel === conf;
                  })
            : yup.string(),
        email_responsavel: mostrarCadastroResponsavelNovo
            ? yup.string().email('Email inválido').required('Email do responsável é obrigatório')
            : yup.string().email('Email inválido'),
        email_responsavel_confirmacao: mostrarCadastroResponsavelNovo
            ? yup
                  .string()
                  .oneOf([yup.ref('email_responsavel'), null], 'Os emails do responsável devem ser iguais')
                  .required('Confirmação do email é obrigatória')
            : yup.string(),
    });

// =============================================
// COMPONENTE
// =============================================

const MenorForm = () => {
    useTokenCheck();
    const { theme } = useJumboTheme();
    const navigate = useNavigate();
    const Swal = useSwalWrapper();
    const { id } = useParams(); // menor_id
    let { state } = useLocation();
    const { hasPermission } = useContext(PermissionContext);

    const lg = useMediaQuery(theme.breakpoints.down('lg'));
    const layoutOptions = React.useMemo(
        () => ({
            sidebar: {
                sx: {
                    [theme.breakpoints.up('lg')]: { position: 'sticky', zIndex: 5, top: 96, minHeight: 'auto' },
                    [theme.breakpoints.down('lg')]: { display: 'none' },
                },
            },
            wrapper: { sx: { alignItems: 'flex-start' } },
        }),
        [theme]
    );

    const [formData, setFormData] = useState(getInitialValues());
    const [responsavelSelecionado, setResponsavelSelecionado] = useState(null);
    const [mostrarCadastroResponsavelNovo, setMostrarCadastroResponsavelNovo] = useState(false);
    const [searchTermResp, setSearchTermResp] = useState('');
    const [resultadosBusca, setResultadosBusca] = useState(null);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [isLoadingCep, setIsLoadingCep] = useState(false);
    const [mesmoCelular, setMesmoCelular] = useState(false);
    const [mesmoEmail, setMesmoEmail] = useState(false);
    const [mesmoEndereco, setMesmoEndereco] = useState(false);
    const [documentoTutela, setDocumentoTutela] = useState(null);
    const [documentoExistente, setDocumentoExistente] = useState(null);
    const [documentoRemovido, setDocumentoRemovido] = useState(false);
    const uploadTutelaRef = useRef(null);
    const erroResponsavelRef = useRef(null);

    const canCreate = hasPermission('Pessoas', 'criar');
    const canEdit = hasPermission('Pessoas', 'editar');
    const isDisabled = id ? !canEdit : !canCreate;
    const menorLiberado = !!(id || responsavelSelecionado || mostrarCadastroResponsavelNovo);

    // Zera todo o formulário do menor ao trocar/remover o responsável selecionado,
    // já que os dados preenchidos (inclusive os copiados do responsável anterior via
    // checkboxes "usar dados do responsável") não devem ficar associados ao responsável errado.
    const limparFormularioMenor = (resetForm) => {
        setMesmoCelular(false);
        setMesmoEmail(false);
        setMesmoEndereco(false);
        setDocumentoTutela(null);
        setDocumentoExistente(null);
        setDocumentoRemovido(false);
        resetForm({ values: getInitialValues() });
    };

    const toast = (variant, message) => {
        const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            onOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            },
        });
        Toast.fire({ icon: variant, title: message, showCloseButton: true, closeButtonAriaLabel: 'Fechar' });
    };

    // ---- Carregar menor no modo edição ----
    useEffect(() => {
        if (!id) return;
        ApiService.get(`/menores/${id}`)
            .then((response) => {
                const m = response.data;
                const p = m.pessoa;

                // Buscar documento de tutela existente
                ApiService.get(`/menores/documentos/${p.pessoa_id}`)
                    .then((docRes) => {
                        const tutela = docRes.data?.find((d) => d.tipo === 'tutela');
                        if (tutela) setDocumentoExistente(tutela);
                    })
                    .catch(() => {});

                const ce = m.contatos_emergencia || [];
                const pa = m.pessoas_autorizadas || [];

                const getContato = (arr, ordem, campo) =>
                    arr.find((c) => c.ordem === ordem)?.[campo] || '';

                const resp = m.responsavel || null;
                setResponsavelSelecionado(resp);
                // Detectar flags de herança a partir dos dados salvos
                // Detecção robusta: celular vazio/null/sentinela '05511' = herda do responsável
                const isMesmoCelular = (!p.celular || p.celular === '05511') && !!resp?.celular && resp.celular !== '05511';
                const isMesmoEmail = !p.email && !!resp?.email;
                const isMesmoEndereco = !!resp?.CEP && !!p.CEP && p.CEP === resp.CEP;
                setMesmoCelular(isMesmoCelular);
                setMesmoEmail(isMesmoEmail);
                setMesmoEndereco(isMesmoEndereco);
                setFormData({
                    nome: p.nome || '',
                    nome_social: p.nome_social || '',
                    data_nascimento: formatDateForInput(p.data_nascimento),
                    genero: p.genero || '',
                    cpf: p.cpf || '',
                    rg: p.rg || '',
                    celular: isMesmoCelular ? (resp.celular || '') : (p.celular || ''),
                    telefone: p.telefone || '',
                    email: isMesmoEmail ? (resp.email || '') : (p.email || ''),
                    pais: p.pais || 'BR',
                    CEP: isMesmoEndereco ? (resp.CEP || '') : (p.CEP || ''),
                    endereco: isMesmoEndereco ? (resp.endereco || '') : (p.endereco || ''),
                    numero: isMesmoEndereco ? (resp.numero || '') : (p.numero || ''),
                    complemento: isMesmoEndereco ? (resp.complemento || '') : (p.complemento || ''),
                    bairro: isMesmoEndereco ? (resp.bairro || '') : (p.bairro || ''),
                    cidade: isMesmoEndereco ? (resp.cidade || '') : (p.cidade || ''),
                    uf: isMesmoEndereco ? (resp.uf || '') : (p.uf || ''),
                    grau_parentesco: m.grau_parentesco || '',
                    deficiencias: m.deficiencias?.tipos || [],
                    outra_deficiencia: m.deficiencias?.outra || '',
                    alergias: m.alergias?.tipos || [],
                    outra_alergia: m.alergias?.outra || '',
                    problemas_saude: m.problemas_saude?.tipos || [],
                    outro_problema_saude: m.problemas_saude?.outro || '',
                    transtornos: m.transtornos?.tipos || [],
                    outro_transtorno: m.transtornos?.outro || '',
                    medicacao_continua: m.medicacao_continua || false,
                    medicamentos: m.medicamentos || '',
                    pode_ir_sozinho: m.pode_ir_sozinho || false,
                    contato_emergencia_1_nome: getContato(ce, 1, 'nome'),
                    contato_emergencia_1_parentesco: getContato(ce, 1, 'parentesco'),
                    contato_emergencia_1_celular: getContato(ce, 1, 'celular'),
                    contato_emergencia_2_nome: getContato(ce, 2, 'nome'),
                    contato_emergencia_2_parentesco: getContato(ce, 2, 'parentesco'),
                    contato_emergencia_2_celular: getContato(ce, 2, 'celular'),
                    contato_emergencia_3_nome: getContato(ce, 3, 'nome'),
                    contato_emergencia_3_parentesco: getContato(ce, 3, 'parentesco'),
                    contato_emergencia_3_celular: getContato(ce, 3, 'celular'),
                    autorizado_1_nome: getContato(pa, 1, 'nome'),
                    autorizado_1_parentesco: getContato(pa, 1, 'parentesco'),
                    autorizado_1_celular: getContato(pa, 1, 'celular'),
                    autorizado_2_nome: getContato(pa, 2, 'nome'),
                    autorizado_2_parentesco: getContato(pa, 2, 'parentesco'),
                    autorizado_2_celular: getContato(pa, 2, 'celular'),
                    autorizado_3_nome: getContato(pa, 3, 'nome'),
                    autorizado_3_parentesco: getContato(pa, 3, 'parentesco'),
                    autorizado_3_celular: getContato(pa, 3, 'celular'),
                    // Responsável novo não se aplica no edit
                    nome_responsavel: '',
                    data_nascimento_responsavel: '',
                    cpf_responsavel: '',
                    rg_responsavel: '',
                    celular_responsavel: '',
                    telefone_responsavel: '',
                    email_responsavel: '',
                    CEP_responsavel: '',
                    endereco_responsavel: '',
                    numero_responsavel: '',
                    complemento_responsavel: '',
                    bairro_responsavel: '',
                    cidade_responsavel: '',
                    uf_responsavel: '',
                });
            })
            .catch(() => toast('error', 'Erro ao carregar dados do menor'));
    }, [id]);

    // ---- Debounce da busca de responsável ----
    useEffect(() => {
        if (!searchTermResp?.trim()) {
            setResultadosBusca(null);
            return;
        }
        const delay = setTimeout(() => {
            setIsLoadingSearch(true);
            ApiService.get('/pessoa', { params: { search: searchTermResp } })
                .then((res) => {
                    setResultadosBusca(res.data);
                    setIsLoadingSearch(false);
                })
                .catch(() => setIsLoadingSearch(false));
        }, 1500);
        return () => clearTimeout(delay);
    }, [searchTermResp]);


    // ---- Busca CEP menor ----
    const checkCep = async (e, setFieldValue) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) return;
        setIsLoadingCep(true);
        axios
            .get(`https://viacep.com.br/ws/${cep}/json/`)
            .then((res) => {
                setFieldValue('endereco', res.data.logradouro || '');
                setFieldValue('bairro', res.data.bairro || '');
                setFieldValue('cidade', res.data.localidade || '');
                setFieldValue('uf', res.data.uf || '');
                setIsLoadingCep(false);
            })
            .catch(() => setIsLoadingCep(false));
    };

    // ---- Busca CEP responsável ----
    const checkCepResp = async (e, setFieldValue) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) return;
        setIsLoadingCep(true);
        axios
            .get(`https://viacep.com.br/ws/${cep}/json/`)
            .then((res) => {
                setFieldValue('endereco_responsavel', res.data.logradouro || '');
                setFieldValue('bairro_responsavel', res.data.bairro || '');
                setFieldValue('cidade_responsavel', res.data.localidade || '');
                setFieldValue('uf_responsavel', res.data.uf || '');
                setIsLoadingCep(false);
            })
            .catch(() => setIsLoadingCep(false));
    };

    // ---- Submit ----
    const getFileUrl = (nome) =>
        `${process.env.REACT_APP_API_KEY}/images/${nome}`;

    const isImage = (nome) => /\.(jpg|jpeg|png)$/i.test(nome);

    const handleRemoverDocumento = () => {
        setDocumentoRemovido(true);
        setDocumentoTutela(null);
    };

    const renderDocumentoTutela = () => {
        // Documento existente no servidor (modo edição), ainda não removido
        const mostrarExistente = documentoExistente && !documentoRemovido && !documentoTutela;
        // Novo arquivo selecionado pelo usuário
        const mostrarNovo = !!documentoTutela;

        return (
            <Box ref={uploadTutelaRef}>
                {mostrarExistente && (
                    <Box display='flex' alignItems='center' gap={1} sx={{ mb: 1 }}>
                        {isImage(documentoExistente.nome) ? (
                            <Box
                                component='a'
                                href={getFileUrl(documentoExistente.nome)}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <Box
                                    component='img'
                                    src={getFileUrl(documentoExistente.nome)}
                                    alt={documentoExistente.nome_label}
                                    sx={{ height: 60, borderRadius: 1, border: '1px solid #ddd', cursor: 'pointer' }}
                                />
                            </Box>
                        ) : (
                            <Typography
                                variant='body2'
                                component='a'
                                href={getFileUrl(documentoExistente.nome)}
                                target='_blank'
                                rel='noopener noreferrer'
                                sx={{ color: 'primary.main' }}
                            >
                                {documentoExistente.nome_label}
                            </Typography>
                        )}
                        <Button size='small' color='error' onClick={handleRemoverDocumento}>
                            Remover
                        </Button>
                    </Box>
                )}

                {mostrarNovo && (
                    <Box display='flex' alignItems='center' gap={1} sx={{ mb: 1 }}>
                        {isImage(documentoTutela.name) ? (
                            <Box
                                component='a'
                                href={URL.createObjectURL(documentoTutela)}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <Box
                                    component='img'
                                    src={URL.createObjectURL(documentoTutela)}
                                    alt={documentoTutela.name}
                                    sx={{ height: 60, borderRadius: 1, border: '1px solid #ddd', cursor: 'pointer' }}
                                />
                            </Box>
                        ) : (
                            <Typography variant='body2' color='text.secondary'>
                                {documentoTutela.name}
                            </Typography>
                        )}
                        <Button size='small' color='error' onClick={() => setDocumentoTutela(null)}>
                            Remover
                        </Button>
                    </Box>
                )}

                {!mostrarExistente && !mostrarNovo && (
                    <Button variant='outlined' size='small' component='label'>
                        Selecionar arquivo
                        <input
                            type='file'
                            accept='.pdf,.jpg,.jpeg,.png'
                            hidden
                            onChange={(e) => {
                                setDocumentoTutela(e.target.files[0] || null);
                                setDocumentoRemovido(false);
                            }}
                        />
                    </Button>
                )}
            </Box>
        );
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        if (!id && !responsavelSelecionado && !mostrarCadastroResponsavelNovo) {
            toast('error', 'Responsável é obrigatório. Selecione um responsável existente antes de continuar.');
            erroResponsavelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setSubmitting(false);
            return;
        }

        const tutelaObrigatoria = values.grau_parentesco === 'representante_legal';
        const tutelaAusente = !documentoTutela && (!documentoExistente || documentoRemovido);
        if (tutelaObrigatoria && tutelaAusente) {
            toast('error', 'O documento de guarda/tutela é obrigatório para Representante Legal');
            uploadTutelaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setSubmitting(false);
            return;
        }

        const dadosPessoa = {
            nome: values.nome,
            nome_social: values.nome_social || null,
            data_nascimento: values.data_nascimento,
            genero: values.genero || null,
            cpf: values.cpf || null,
            rg: values.rg || null,
            celular: mesmoCelular ? '05511' : (values.celular || '05511'),
            telefone: values.telefone || '05511',
            email: mesmoEmail ? null : (values.email || null),
            pais: values.pais || 'BR',
            CEP: values.CEP || null,
            endereco: values.endereco || null,
            numero: values.numero || null,
            complemento: values.complemento || null,
            bairro: values.bairro || null,
            cidade: values.cidade || null,
            uf: values.uf || null,
        };

        const dadosMenor = {
            responsavel_id: responsavelSelecionado?.pessoa_id || undefined,
            grau_parentesco: values.grau_parentesco,
           /*  deficiencias: values.deficiencias || [],
            alergias: values.alergias || [],
            problemas_saude: values.problemas_saude || [],
            transtornos: values.transtornos || [], */
            medicacao_continua: values.medicacao_continua,
            medicamentos: values.medicamentos || null,
            pode_ir_sozinho: values.pode_ir_sozinho,
            deficiencias: values.deficiencias?.length > 0                                                
                ? { tipos: values.deficiencias, outra: values.outra_deficiencia || '' }                  
                : null,                                                                                  
            alergias: values.alergias?.length > 0                                                        
                ? { tipos: values.alergias, outra: values.outra_alergia || '' }                          
                : null,                                                                                  
            problemas_saude: values.problemas_saude?.length > 0                                          
                ? { tipos: values.problemas_saude, outro: values.outro_problema_saude || '' }            
                : null,                                                                                  
            transtornos: values.transtornos?.length > 0                                                  
                ? { tipos: values.transtornos, outro: values.outro_transtorno || '' }                    
                : null,                      
        };

        const dadosResponsavel = mostrarCadastroResponsavelNovo
            ? {
                  nome: values.nome_responsavel,
                  data_nascimento: values.data_nascimento_responsavel,
                  cpf: values.cpf_responsavel || null,
                  rg: values.rg_responsavel || null,
                  email: values.email_responsavel || null,
                  celular: values.celular_responsavel || '05511',
                  telefone: values.telefone_responsavel || '05511',
                  pais: 'BR',
                  CEP: values.CEP_responsavel || null,
                  endereco: values.endereco_responsavel || null,
                  numero: values.numero_responsavel || null,
                  complemento: values.complemento_responsavel || null,
                  bairro: values.bairro_responsavel || null,
                  cidade: values.cidade_responsavel || null,
                  uf: values.uf_responsavel || null,
                  senha: String(Math.floor(100000 + Math.random() * 900000)),
              }
            : null;

        const contatosEmergencia = buildContatos(values, 'contato_emergencia', 3);
        const pessoasAutorizadas = buildContatos(values, 'autorizado', 3);

        const uploadDocumentoTutela = async (menorId) => {
            if (!documentoTutela || values.grau_parentesco !== 'representante_legal') return;
            const formDataUpload = new FormData();
            formDataUpload.append('docs', documentoTutela);
            formDataUpload.append('menor_id', menorId);
            formDataUpload.append('tipo_0', 'tutela');
            formDataUpload.append('nome_label_0', documentoTutela.name);
            await ApiService.post('/menores/documentos/upload', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        };

        try {
            if (id) {
                await ApiService.put(`/menores/${id}/atualizar`, {
                    dadosPessoa,
                    dadosMenor,
                    contatosEmergencia,
                    pessoasAutorizadas,
                });
                // Se usuário marcou para remover o doc existente, DELETE agora
                if (documentoRemovido && documentoExistente) {
                    await ApiService.delete(`/menores/documentos/${documentoExistente.documento_menor_id}`);
                }
                await uploadDocumentoTutela(id);
                toast('success', 'Menor atualizado com sucesso');
            } else {
                const response = await ApiService.post('/menores', {
                    dadosPessoa,
                    dadosMenor,
                    dadosResponsavel,
                    contatosEmergencia,
                    pessoasAutorizadas,
                    turmas_id: null,
                });
                const menorId = response.data?.data?.menor?.menor_id;
                if (menorId) await uploadDocumentoTutela(menorId);
                toast('success', 'Menor cadastrado com sucesso');
                resetForm({ values: getInitialValues() });
                setResponsavelSelecionado(null);
                setMostrarCadastroResponsavelNovo(false);
                setSearchTermResp('');
                setResultadosBusca(null);
                setMesmoCelular(false);
                setMesmoEmail(false);
                setMesmoEndereco(false);
                setDocumentoTutela(null);
                setDocumentoExistente(null);
                setDocumentoRemovido(false);
            }
        } catch (error) {
            const mensagem = error?.response?.data?.error || 'Ocorreu um erro';
            Swal.fire({ title: 'Erro', text: mensagem, icon: 'error' });
        }

        setSubmitting(false);
    };

    return (
        <JumboContentLayout
            header={
                <HeaderBreadcrumbs
                    id={id}
                    title='Menores'
                    subtitle={id ? 'Editar Menor' : 'Cadastro de Menor'}
                    titleUrl='/app/listar-menores'
                />
            }
            layoutOptions={layoutOptions}
        >
            {lg && <Stack spacing={2} direction='row' sx={{ mb: 3, mt: -2 }} />}

            <Paper sx={{ p: '40px' }}>
                {state?.backUrl && (
                    <Button
                        color='success'
                        variant='contained'
                        size='large'
                        sx={{ maxWidth: { md: '200px' }, mb: '2rem' }}
                        onClick={() => navigate(state.backUrl)}
                    >
                        Voltar
                    </Button>
                )}

                <Formik
                    initialValues={formData}
                    validationSchema={getValidationSchema(mostrarCadastroResponsavelNovo, menorLiberado)}
                    enableReinitialize
                    validateOnChange={false}
                    validateOnBlur={true}
                    onSubmit={handleSubmit}
                >
                    {({ values, isSubmitting, handleChange, setFieldValue, resetForm, errors }) => {
                        // Fonte única dos dados do responsável para os checkboxes "usar dados do
                        // responsável": vem do responsável existente selecionado na busca, ou dos
                        // campos do responsável novo sendo digitado — os dois estados são mutuamente
                        // exclusivos, nunca ficam ativos ao mesmo tempo.
                        const fonteResponsavel = responsavelSelecionado
                            ? {
                                  celular: responsavelSelecionado.celular,
                                  email: responsavelSelecionado.email,
                                  CEP: responsavelSelecionado.CEP,
                                  endereco: responsavelSelecionado.endereco,
                                  numero: responsavelSelecionado.numero,
                                  complemento: responsavelSelecionado.complemento,
                                  bairro: responsavelSelecionado.bairro,
                                  cidade: responsavelSelecionado.cidade,
                                  uf: responsavelSelecionado.uf,
                              }
                            : mostrarCadastroResponsavelNovo
                            ? {
                                  celular: values.celular_responsavel,
                                  email: values.email_responsavel,
                                  CEP: values.CEP_responsavel,
                                  endereco: values.endereco_responsavel,
                                  numero: values.numero_responsavel,
                                  complemento: values.complemento_responsavel,
                                  bairro: values.bairro_responsavel,
                                  cidade: values.cidade_responsavel,
                                  uf: values.uf_responsavel,
                              }
                            : null;

                        return (
                        <Form style={{ width: '100%' }} noValidate autoComplete='off'>
                            <ScrollToFirstError />

                            {/* ========== SEÇÃO RESPONSÁVEL ========== */}
                            {/* <Typography variant='h5' fontWeight='bold' sx={{ mb: 2 }}>
                                Responsável
                            </Typography> */}

                            {id ? (
                                /* Modo edição: exibe nome do responsável + grau de parentesco editável */
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={6}>
                                        <JumboTextField
                                            fullWidth
                                            name='_responsavel_nome'
                                            label='Responsável'
                                            value={
                                                responsavelSelecionado?.nome
                                                    ? `${responsavelSelecionado.nome}${
                                                          GRAU_PARENTESCO_LABELS[values.grau_parentesco]
                                                              ? ` (${GRAU_PARENTESCO_LABELS[values.grau_parentesco]})`
                                                              : ''
                                                      }`
                                                    : '-'
                                            }
                                            disabled
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel shrink id='grau_parentesco_edit_label'>Grau de Parentesco *</InputLabel>
                                            <JumboSelectField
                                                labelId='grau_parentesco_edit_label'
                                                id='grau_parentesco_edit_label'
                                                name='grau_parentesco'
                                                label='Grau de Parentesco *'
                                                disabled={isDisabled}
                                                notched
                                            >
                                                <MenuItem value='pai'>Pai/Padrasto</MenuItem>
                                                <MenuItem value='mae'>Mãe/Madrasta</MenuItem>
                                                <MenuItem value='avo'>Avô/Avó</MenuItem>
                                                <MenuItem value='tio'>Tio/Tia</MenuItem>
                                                <MenuItem value='irmao'>Irmão/Irmã</MenuItem>
                                                <MenuItem value='representante_legal'>Representante Legal</MenuItem>
                                                <MenuItem value='outro'>Outro</MenuItem>
                                            </JumboSelectField>
                                        </FormControl>
                                    </Grid>
                                    {values.grau_parentesco === 'representante_legal' && (
                                        <Grid item xs={12}>
                                            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                                                Documento de guarda/tutela (obrigatório para Representante Legal)
                                            </Typography>
                                            {renderDocumentoTutela()}
                                        </Grid>
                                    )}
                                </Grid>
                            ) : (
                                /* Modo criação: busca ou cadastro novo */
                                <Box ref={erroResponsavelRef} sx={{ mb: 3 }}>
                                    {!mostrarCadastroResponsavelNovo && (
                                        <>
                                            <Grid container spacing={2} alignItems='flex-start' sx={{ mb: 1 }}>
                                                <Grid item xs={12} md={6}>
                                                    <JumboSearchClasses
                                                        value={searchTermResp}
                                                        onChange={(e) => setSearchTermResp(e.target.value)}
                                                        placeholder='Busque por nome, CPF ou e-mail do responsável'
                                                        sx={{ width: '100%' }}
                                                    />
                                                </Grid>
                                            </Grid>

                                            {isLoadingSearch && (
                                                <Box display='flex' justifyContent='center' my={1}>
                                                    <CircularProgress size={24} />
                                                </Box>
                                            )}

                                            {resultadosBusca != null && resultadosBusca.length === 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        flexWrap: 'wrap',
                                                        gap: 1,
                                                        p: 1.5,
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Typography color='text.secondary'>
                                                        Nenhum responsável encontrado com esses dados
                                                    </Typography>
                                                    <Button
                                                        size='small'
                                                        variant='contained'
                                                        color='warning'
                                                        onClick={() => {
                                                            setMostrarCadastroResponsavelNovo(true);
                                                            setResultadosBusca(null);
                                                            setSearchTermResp('');
                                                            if (responsavelSelecionado) {
                                                                setResponsavelSelecionado(null);
                                                                limparFormularioMenor(resetForm);
                                                            }
                                                        }}
                                                    >
                                                        Cadastrar novo responsável
                                                    </Button>
                                                </Box>
                                            )}

                                            {resultadosBusca && resultadosBusca.length > 0 && (
                                                <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                                    {resultadosBusca.map((pessoa) => (
                                                        <ListItem
                                                            key={pessoa.pessoa_id}
                                                            divider
                                                            secondaryAction={
                                                                <Button
                                                                    size='small'
                                                                    variant='contained'
                                                                    color='success'
                                                                    onClick={() => {
                                                                        if (
                                                                            responsavelSelecionado &&
                                                                            responsavelSelecionado.pessoa_id !== pessoa.pessoa_id
                                                                        ) {
                                                                            limparFormularioMenor(resetForm);
                                                                        }
                                                                        setResponsavelSelecionado(pessoa);
                                                                        setResultadosBusca(null);
                                                                        setSearchTermResp('');
                                                                    }}
                                                                >
                                                                    Selecionar
                                                                </Button>
                                                            }
                                                        >
                                                            <ListItemText
                                                                primary={pessoa.nome}
                                                                secondary={`CPF: ${pessoa.cpf || '-'} | Celular: ${pessoa.celular || '-'}`}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            )}

                                            {responsavelSelecionado && (
                                                <Grid container spacing={2} alignItems='flex-start' sx={{ mt: 1 }}>
                                                    <Grid item xs={12} md={4}>
                                                        <Box
                                                            sx={{
                                                                p: 1.2,
                                                                border: 2,
                                                                borderColor: 'success.main',
                                                                borderRadius: 1,
                                                                bgcolor: 'success.50',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Typography fontWeight='bold' sx={{ flex: '1 1 auto', fontSize: '0.9rem' }}>
                                                                {responsavelSelecionado.nome}
                                                            </Typography>
                                                            <Button
                                                                size='small'
                                                                color='error'
                                                                onClick={() => {
                                                                    setResponsavelSelecionado(null);
                                                                    limparFormularioMenor(resetForm);
                                                                }}
                                                            >
                                                                Remover
                                                            </Button>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} md={3}>
                                                        <FormControl fullWidth>
                                                            <InputLabel shrink id='grau_parentesco_label'>Grau de Parentesco *</InputLabel>
                                                            <JumboSelectField
                                                                labelId='grau_parentesco_label'
                                                                id='grau_parentesco_label'
                                                                name='grau_parentesco'
                                                                label='Grau de Parentesco *'
                                                                disabled={isDisabled}
                                                                notched
                                                            >
                                                                <MenuItem value='pai'>Pai/Padrasto</MenuItem>
                                                                <MenuItem value='mae'>Mãe/Madrasta</MenuItem>
                                                                <MenuItem value='avo'>Avô/Avó</MenuItem>
                                                                <MenuItem value='tio'>Tio/Tia</MenuItem>
                                                                <MenuItem value='irmao'>Irmão/Irmã</MenuItem>
                                                                <MenuItem value='representante_legal'>Representante Legal</MenuItem>
                                                                <MenuItem value='outro'>Outro</MenuItem>
                                                            </JumboSelectField>
                                                        </FormControl>
                                                    </Grid>
                                                    {values.grau_parentesco === 'representante_legal' && (
                                                        <Grid item xs={12}>
                                                            <Box sx={{ mb: 2 }}>
                                                                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                                                                    Documento de guarda/tutela (obrigatório para Representante Legal)
                                                                </Typography>
                                                                {renderDocumentoTutela()}
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            )}
                                        </>
                                    )}

                                    {mostrarCadastroResponsavelNovo && (
                                        <Box sx={{ border: 1, borderColor: 'warning.main', borderRadius: 1, p: 2, mt: 1 }}>
                                            <Box display='flex' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
                                                <Typography variant='subtitle1' fontWeight='bold'>
                                                    Dados do Novo Responsável
                                                </Typography>
                                                <Button
                                                    size='small'
                                                    onClick={() => setMostrarCadastroResponsavelNovo(false)}
                                                >
                                                    Voltar para busca
                                                </Button>
                                            </Box>

                                            {/* ========== DADOS PESSOAIS ========== */}
                                            <Typography variant='subtitle2' fontWeight='bold' sx={{ mb: 2 }}>
                                                Dados Pessoais
                                            </Typography>
                                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                                <Grid item xs={12} md={6}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='nome_responsavel'
                                                        label='Nome Completo do Responsável *'
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth>
                                                        <InputLabel shrink id='grau_parentesco_novo_label'>Grau de Parentesco *</InputLabel>
                                                        <JumboSelectField
                                                            labelId='grau_parentesco_novo_label'
                                                            id='grau_parentesco_novo_label'
                                                            name='grau_parentesco'
                                                            label='Grau de Parentesco *'
                                                            disabled={isDisabled}
                                                            notched
                                                        >
                                                            <MenuItem value='pai'>Pai/Padrasto</MenuItem>
                                                            <MenuItem value='mae'>Mãe/Madrasta</MenuItem>
                                                            <MenuItem value='avo'>Avô/Avó</MenuItem>
                                                            <MenuItem value='tio'>Tio/Tia</MenuItem>
                                                            <MenuItem value='irmao'>Irmão/Irmã</MenuItem>
                                                            <MenuItem value='representante_legal'>Representante Legal</MenuItem>
                                                            <MenuItem value='outro'>Outro</MenuItem>
                                                        </JumboSelectField>
                                                    </FormControl>
                                                </Grid>
                                                {values.grau_parentesco === 'representante_legal' && (
                                                    <Grid item xs={12}>
                                                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                                                            Documento de guarda/tutela (obrigatório para Representante Legal)
                                                        </Typography>
                                                        {renderDocumentoTutela()}
                                                    </Grid>
                                                )}
                                                <Grid item xs={12} md={6}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='data_nascimento_responsavel'
                                                        type='date'
                                                        label='Data de Nascimento do Responsável *'
                                                        InputLabelProps={{ shrink: true }}
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <InputMask
                                                        mask='999.999.999-99'
                                                        value={values.cpf_responsavel}
                                                        onChange={handleChange}
                                                        disabled={isDisabled}
                                                    >
                                                        {(inputProps) => (
                                                            <JumboTextField
                                                                {...inputProps}
                                                                fullWidth
                                                                name='cpf_responsavel'
                                                                label='CPF do Responsável'
                                                                disabled={isDisabled}
                                                            />
                                                        )}
                                                    </InputMask>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='rg_responsavel'
                                                        label='RG do Responsável'
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                            </Grid>

                                            {/* ========== CONTATO ========== */}
                                            <Typography variant='subtitle2' fontWeight='bold' sx={{ mb: 2 }}>
                                                Contato
                                            </Typography>
                                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                                <Grid item xs={12} md={6}>
                                                    <InputMask
                                                        mask='+999(99)99999-9999'
                                                        value={values.celular_responsavel}
                                                        onChange={handleChange}
                                                        disabled={isDisabled}
                                                    >
                                                        {(inputProps) => (
                                                            <JumboTextField
                                                                {...inputProps}
                                                                fullWidth
                                                                name='celular_responsavel'
                                                                label='Celular do Responsável (DDI, DDD e número) *'
                                                                InputLabelProps={{ shrink: true }}
                                                                disabled={isDisabled}
                                                            />
                                                        )}
                                                    </InputMask>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <InputMask
                                                        mask='+999(99)99999-9999'
                                                        value={values.celular_responsavel_confirmacao}
                                                        onChange={handleChange}
                                                        onPaste={(e) => e.preventDefault()}
                                                        disabled={isDisabled}
                                                    >
                                                        {(inputProps) => (
                                                            <JumboTextField
                                                                {...inputProps}
                                                                fullWidth
                                                                name='celular_responsavel_confirmacao'
                                                                label='Confirmar Celular do Responsável *'
                                                                InputLabelProps={{ shrink: true }}
                                                                disabled={isDisabled}
                                                            />
                                                        )}
                                                    </InputMask>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <InputMask
                                                        mask='(99)9999-9999'
                                                        value={values.telefone_responsavel}
                                                        onChange={handleChange}
                                                        disabled={isDisabled}
                                                    >
                                                        {(inputProps) => (
                                                            <JumboTextField
                                                                {...inputProps}
                                                                fullWidth
                                                                name='telefone_responsavel'
                                                                label='Telefone Fixo do Responsável (opcional)'
                                                                InputLabelProps={{ shrink: true }}
                                                                disabled={isDisabled}
                                                            />
                                                        )}
                                                    </InputMask>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='email_responsavel'
                                                        label='Email do Responsável *'
                                                        type='email'
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='email_responsavel_confirmacao'
                                                        label='Confirmar Email do Responsável *'
                                                        type='email'
                                                        disabled={isDisabled}
                                                        onPaste={(e) => e.preventDefault()}
                                                    />
                                                </Grid>
                                            </Grid>

                                            {/* ========== ENDEREÇO ========== */}
                                            <Typography variant='subtitle2' fontWeight='bold' sx={{ mb: 2 }}>
                                                Endereço
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={3}>
                                                    <InputMask
                                                        mask='99999-999'
                                                        value={values.CEP_responsavel}
                                                        onChange={handleChange}
                                                        onBlur={(e) => checkCepResp(e, setFieldValue)}
                                                        disabled={isDisabled}
                                                    >
                                                        {(inputProps) => (
                                                            <JumboTextField
                                                                {...inputProps}
                                                                fullWidth
                                                                name='CEP_responsavel'
                                                                label='CEP do Responsável'
                                                                disabled={isDisabled}
                                                            />
                                                        )}
                                                    </InputMask>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='endereco_responsavel'
                                                        label='Endereço do Responsável'
                                                        InputLabelProps={{ shrink: true }}
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='numero_responsavel'
                                                        label='Número'
                                                        InputLabelProps={{ shrink: true }}
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='complemento_responsavel'
                                                        label='Complemento'
                                                        InputLabelProps={{ shrink: true }}
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='bairro_responsavel'
                                                        label='Bairro'
                                                        InputLabelProps={{ shrink: true }}
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <FormControl fullWidth>
                                                        <InputLabel id='uf_resp_label' shrink>UF</InputLabel>
                                                        <JumboSelectField
                                                            labelId='uf_resp_label'
                                                            name='uf_responsavel'
                                                            label='UF'
                                                            disabled={isDisabled}
                                                        >
                                                            {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                                                                <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                                            ))}
                                                        </JumboSelectField>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <JumboTextField
                                                        fullWidth
                                                        name='cidade_responsavel'
                                                        label='Cidade'
                                                        InputLabelProps={{ shrink: true }}
                                                        disabled={isDisabled}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {!menorLiberado && (
                                <Alert severity='info' sx={{ mt: 2, display: 'inline-flex', width: 'fit-content', maxWidth: '100%' }}>
                                    Selecione um responsável existente ou cadastre um novo responsável acima para
                                    liberar os dados do menor.
                                </Alert>
                            )}

                            {menorLiberado && (
                            <>
                            <Divider sx={{ my: 3 }} />

                            {/* ========== SEÇÃO DADOS DO MENOR ========== */}
                            <Typography variant='h5' fontWeight='bold' sx={{ mb: 2 }}>
                                Dados do Menor
                            </Typography>

                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <JumboTextField
                                        fullWidth
                                        name='nome'
                                        label='Nome Completo *'
                                        disabled={isDisabled}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <JumboTextField
                                        fullWidth
                                        name='nome_social'
                                        label='Nome Social (opcional)'
                                        disabled={isDisabled}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <JumboTextField
                                        fullWidth
                                        name='data_nascimento'
                                        type='date'
                                        label='Data de Nascimento *'
                                        InputLabelProps={{ shrink: true }}
                                        disabled={isDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink id='genero_label'>Gênero</InputLabel>
                                        <JumboSelectField
                                            labelId='genero_label'
                                            id='genero_label'
                                            name='genero'
                                            label='Gênero'
                                            disabled={isDisabled}
                                            notched
                                        >
                                            <MenuItem value='masculino'>Masculino</MenuItem>
                                            <MenuItem value='feminino'>Feminino</MenuItem>
                                            <MenuItem value='outro'>Outro</MenuItem>
                                            <MenuItem value='prefere_nao_informar'>Prefere não Informar</MenuItem>
                                        </JumboSelectField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>

                                    <InputMask
                                        mask='999.999.999-99'
                                        value={values.cpf}
                                        onChange={handleChange}
                                        disabled={isDisabled}
                                        disableUnderline={isDisabled}
                                    >
                                        {(inputProps) => (
                                            <JumboTextField
                                                {...inputProps}
                                                fullWidth
                                                name='cpf'
                                                label='CPF (opcional)'
                                                disabled={inputProps.disableUnderline}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    </InputMask>
                                       
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <JumboTextField
                                        fullWidth
                                        name='rg'
                                        label='RG'
                                        disabled={isDisabled}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <InputMask
                                        mask='+999(99)99999-9999'
                                        value={values.celular}
                                        onChange={handleChange}
                                        disabled={isDisabled || mesmoCelular}
                                        disableUnderline={isDisabled || mesmoCelular}
                                    >
                                        {(inputProps) => (
                                            <JumboTextField
                                                {...inputProps}
                                                fullWidth
                                                name='celular'
                                                label='Celular (opcional)'
                                                disabled={inputProps.disableUnderline}
                                                helperText={mesmoCelular ? 'Usando celular do responsável' : 'Se não informado, o celular do responsável será utilizado'}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    </InputMask>
                                    {fonteResponsavel?.celular && fonteResponsavel.celular !== '05511' && (
                                        <FormControlLabel
                                            sx={{ mt: 0.5 }}
                                            control={
                                                <Checkbox
                                                    checked={mesmoCelular}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setMesmoCelular(checked);
                                                        // Marcado: exibe o celular do responsável (campo desabilitado);
                                                        // o backend grava o sentinela. Desmarcado: limpa para edição.
                                                        setFieldValue('celular', checked ? fonteResponsavel.celular : '');
                                                    }}
                                                    disabled={isDisabled}
                                                />
                                            }
                                            label={`Usar celular do responsável (${fonteResponsavel.celular})`}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <JumboTextField
                                        fullWidth
                                        name='email'
                                        label='Email (opcional)'
                                        type='email'
                                        disabled={isDisabled || mesmoEmail}
                                        helperText={mesmoEmail ? 'Usando email do responsável' : 'Se não informado, as comunicações serão enviadas ao responsável'}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                    {fonteResponsavel?.email && (
                                        <FormControlLabel
                                            sx={{ mt: 0.5 }}
                                            control={
                                                <Checkbox
                                                    checked={mesmoEmail}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setMesmoEmail(checked);
                                                        // Marcado: exibe o email do responsável (campo desabilitado);
                                                        // o backend grava null. Desmarcado: limpa para edição.
                                                        setFieldValue('email', checked ? fonteResponsavel.email : '');
                                                    }}
                                                    disabled={isDisabled}
                                                />
                                            }
                                            label={`Usar email do responsável (${fonteResponsavel.email})`}
                                        />
                                    )}
                                </Grid>

                                {/* Checkbox endereço do responsável */}
                                {fonteResponsavel?.CEP && (
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={mesmoEndereco}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setMesmoEndereco(checked);
                                                        if (checked) {
                                                            setFieldValue('CEP', fonteResponsavel.CEP || '');
                                                            setFieldValue('endereco', fonteResponsavel.endereco || '');
                                                            setFieldValue('numero', fonteResponsavel.numero || '');
                                                            setFieldValue('bairro', fonteResponsavel.bairro || '');
                                                            setFieldValue('cidade', fonteResponsavel.cidade || '');
                                                            setFieldValue('uf', fonteResponsavel.uf || '');
                                                            setFieldValue('complemento', fonteResponsavel.complemento || '');
                                                        } else {
                                                            setFieldValue('CEP', '');
                                                            setFieldValue('endereco', '');
                                                            setFieldValue('numero', '');
                                                            setFieldValue('bairro', '');
                                                            setFieldValue('cidade', '');
                                                            setFieldValue('uf', '');
                                                            setFieldValue('complemento', '');
                                                        }
                                                    }}
                                                    disabled={isDisabled}
                                                />
                                            }
                                            label={`Usar endereço do responsável (${fonteResponsavel.endereco || ''}, ${fonteResponsavel.numero || ''} - ${fonteResponsavel.cidade || ''})`}
                                        />
                                    </Grid>
                                )}

                                {/* Endereço */}
                                <Grid item xs={12} md={3}>
                                    <InputMask
                                        mask='99999-999'
                                        value={values.CEP}
                                        onChange={handleChange}
                                        onBlur={(e) => !mesmoEndereco && checkCep(e, setFieldValue)}
                                        disabled={isDisabled || mesmoEndereco}
                                        disableUnderline={isDisabled || mesmoEndereco}
                                    >
                                        {(inputProps) => (
                                            <JumboTextField
                                                {...inputProps}
                                                fullWidth
                                                name='CEP'
                                                label='CEP'
                                                disabled={inputProps.disableUnderline}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                </Grid>
                                {isLoadingCep && (
                                    <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CircularProgress size={24} />
                                    </Grid>
                                )}
                                <Grid item xs={12} md={6}>
                                    <JumboTextField
                                        fullWidth
                                        name='endereco'
                                        label='Endereço'
                                        InputLabelProps={{ shrink: true }}
                                        disabled={isDisabled || mesmoEndereco}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <JumboTextField
                                        fullWidth
                                        name='numero'
                                        label='Número'
                                        InputLabelProps={{ shrink: true }}
                                        disabled={isDisabled || mesmoEndereco}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <JumboTextField
                                        fullWidth
                                        name='bairro'
                                        label='Bairro'
                                        InputLabelProps={{ shrink: true }}
                                        disabled={isDisabled || mesmoEndereco}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <JumboTextField
                                        fullWidth
                                        name='cidade'
                                        label='Cidade'
                                        InputLabelProps={{ shrink: true }}
                                        disabled={isDisabled || mesmoEndereco}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink id='uf_label'>UF</InputLabel>
                                        <JumboSelectField
                                            labelId='uf_label'
                                            id='uf_label'
                                            name='uf'
                                            label='UF'
                                            disabled={isDisabled || mesmoEndereco}
                                            notched
                                        >
                                            {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                                                <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                            ))}
                                        </JumboSelectField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <JumboTextField
                                        fullWidth
                                        name='complemento'
                                        label='Complemento'
                                        disabled={isDisabled || mesmoEndereco}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* ========== ACCORDION HISTÓRICO MÉDICO ========== */}
                            <Accordion defaultExpanded sx={{ mb: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant='h6' fontWeight='bold'>
                                        Histórico Médico
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <CheckboxSection
                                                title='O menor apresenta alguma deficiência?'
                                                options={OPCOES_DEFICIENCIAS}
                                                selectedValues={values.deficiencias}
                                                fieldName='deficiencias'
                                                otherFieldName='outra_deficiencia'
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                handleChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <CheckboxSection
                                                title='O menor possui alguma alergia?'
                                                options={OPCOES_ALERGIAS}
                                                selectedValues={values.alergias}
                                                fieldName='alergias'
                                                otherFieldName='outra_alergia'
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                handleChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <CheckboxSection
                                                title='O menor possui algum problema de saúde?'
                                                options={OPCOES_PROBLEMAS_SAUDE}
                                                selectedValues={values.problemas_saude}
                                                fieldName='problemas_saude'
                                                otherFieldName='outro_problema_saude'
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                handleChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <CheckboxSection
                                                title='O menor possui algum transtorno?'
                                                options={OPCOES_TRANSTORNOS}
                                                selectedValues={values.transtornos}
                                                fieldName='transtornos'
                                                otherFieldName='outro_transtorno'
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                handleChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.medicacao_continua}
                                                        onChange={(e) =>
                                                            setFieldValue('medicacao_continua', e.target.checked)
                                                        }
                                                        disabled={isDisabled}
                                                    />
                                                }
                                                label='O menor faz uso de medicação contínua?'
                                            />
                                        </Grid>
                                        {values.medicacao_continua && (
                                            <Grid item xs={12}>
                                                <JumboTextField
                                                    fullWidth
                                                    name='medicamentos'
                                                    label='Quais medicamentos?'
                                                    multiline
                                                    maxRows={4}
                                                    inputProps={{ maxLength: 300 }}
                                                    helperText={`${values.medicamentos?.length || 0}/300 caracteres`}
                                                    disabled={isDisabled}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.pode_ir_sozinho}
                                                        onChange={(e) =>
                                                            setFieldValue('pode_ir_sozinho', e.target.checked)
                                                        }
                                                        disabled={isDisabled}
                                                    />
                                                }
                                                label='O menor pode ir embora sozinho?'
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* ========== ACCORDION CONTATOS ========== */}
                            <Accordion defaultExpanded sx={{ mb: 3 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant='h6' fontWeight='bold'>
                                        Contatos de Emergência e Pessoas Autorizadas
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 1 }}>
                                        Contatos de Emergência
                                    </Typography>
                                    {[1, 2, 3].map((n) => (
                                        <ContatoCard
                                            key={`emergencia_${n}`}
                                            numero={n}
                                            values={values}
                                            handleChange={handleChange}
                                            tipo='emergencia'
                                            obrigatorio={n === 1}
                                        />
                                    ))}

                                    <Typography variant='subtitle1' fontWeight='bold' sx={{ mt: 2, mb: 1 }}>
                                        Pessoas Autorizadas a Buscar
                                    </Typography>
                                    {[1, 2, 3].map((n) => (
                                        <ContatoCard
                                            key={`autorizado_${n}`}
                                            numero={n}
                                            values={values}
                                            handleChange={handleChange}
                                            tipo='autorizado'
                                            obrigatorio={false}
                                        />
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                            </>
                            )}

                            {/* ========== SUBMIT ========== */}
                            {menorLiberado && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <LoadingButton
                                        color='success'
                                        type='submit'
                                        variant='contained'
                                        size='large'
                                        loading={isSubmitting}
                                        disabled={isDisabled}
                                        sx={{ width: { xs: '100%', md: '200px' } }}
                                    >
                                        {id ? 'Atualizar' : 'Cadastrar'}
                                    </LoadingButton>
                                </Box>
                            )}
                        </Form>
                        );
                    }}
                </Formik>
            </Paper>
        </JumboContentLayout>
    );
};

export default MenorForm;
